import * as THREE from 'three';
import type { PopupLayer } from '../scenes/PopupScene';
import { alphaGrid, blurredAlphaTexture } from '../fx/layerAlpha';

export async function loadTexture(renderer: THREE.WebGLRenderer, url: string): Promise<THREE.Texture> {
  const tex = await new THREE.TextureLoader().loadAsync(url);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
  return tex;
}

/** 起立前替补用的遮罩分辨率：只决定原图与本层之间过渡的软硬，两边颜色相同，512 足够 */
const ABOVE_SIZE = 512;

/**
 * 每层的"上方并集"alpha：第 k 层之上（更近）所有层的覆盖范围。分层后每层只含自己那段深度，
 * 更近的层还没出现时，最上面可见的那层要在这块范围里替补画原图，平放时画面才与原图一致。
 */
function aboveMasks(imgs: (CanvasImageSource & { width: number; height: number })[]): (THREE.Texture | null)[] {
  const c = document.createElement('canvas');
  c.width = c.height = ABOVE_SIZE;
  const g = c.getContext('2d')!;
  const out: (THREE.Texture | null)[] = new Array(imgs.length + 1).fill(null);
  // 从最近一层往远处累积；out[k] = 层 k+1..n 的并集（图片索引 = 层号 - 1）
  for (let k = imgs.length; k >= 1; k--) {
    g.drawImage(imgs[k - 1], 0, 0, ABOVE_SIZE, ABOVE_SIZE);
    const snap = document.createElement('canvas');
    snap.width = snap.height = ABOVE_SIZE;
    snap.getContext('2d')!.drawImage(c, 0, 0);
    const t = new THREE.CanvasTexture(snap);
    t.minFilter = THREE.LinearFilter;
    t.magFilter = THREE.LinearFilter;
    t.generateMipmaps = false;
    out[k - 1] = t;
  }
  return out;
}

/**
 * 载入原图 + 一套立体书分层。原图（拼图和起立前的平放画面用它）失败则整体抛错（地点卡显示"加载失败，重试"）。
 * 第 0 层是背板（近处内容已在流水线里补画掉），1.. 层各自只含一段深度。
 * 任何一层缺失时（spec §9）只用原图做浮起和扫光，跳过立起。原图由立体书持有并释放。
 */
export async function loadLayers(
  renderer: THREE.WebGLRenderer,
  image: string,
  files: string[],
): Promise<{ picture: THREE.Texture; layers: PopupLayer[] }> {
  const picture = await loadTexture(renderer, image);
  const loaded = await Promise.all(
    files.map((f) =>
      loadTexture(renderer, f).catch((err) => {
        console.warn(`popup layer failed to load, skipping the pop-up: ${f}`, err);
        return null;
      }),
    ),
  );
  if (loaded.length < 2 || loaded.some((t) => t === null)) {
    loaded.forEach((t) => t?.dispose());
    return { picture, layers: [{ texture: picture, alpha: null, blur: null, above: null }] };
  }
  const [plate, ...fg] = loaded as THREE.Texture[];
  const imgs = fg.map((t) => t.image as HTMLImageElement);
  const above = aboveMasks(imgs);
  return {
    picture,
    layers: [
      { texture: plate, alpha: null, blur: null, above: above[0], picture },
      ...fg.map((t, i) => ({ texture: t, alpha: alphaGrid(imgs[i]), blur: blurredAlphaTexture(imgs[i]), above: above[i + 1] })),
    ],
  };
}
